import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    // Check for token in multiple places (demo mode stores as both 'token' and 'demo_token')
    const token = localStorage.getItem('token') || localStorage.getItem('demo_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth API
export const authAPI = {
  register: (name, email, password) => 
    api.post('/auth/register', { name, email, password }),
  
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  demoLogin: () => api.post('/auth/demo'),
  
  getMe: () => api.get('/auth/me')
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/team'),
  getOne: (id) => api.get(`/team/${id}`),
  create: (data) => api.post('/team', data),
  update: (id, data) => api.put(`/team/${id}`, data)
};

// Project API
export const projectAPI = {
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data)
};

// Task API
export const taskAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data)
};

// Step API
export const stepAPI = {
  create: (data) => api.post('/steps', data),
  update: (id, data) => api.put(`/steps/${id}`, data)
};

// Metrics API
export const metricsAPI = {
  getPulse: () => api.get('/metrics/pulse'),
  getInsights: () => api.get('/metrics/insights')
};

// Insights API (Executive Dashboard)
export const insightsAPI = {
  getDashboard: () => api.get('/insights/dashboard'),
  getHealthScore: () => api.get('/insights/health-score'),
  getCognitiveLoad: () => api.get('/insights/cognitive-load'),
  getRisks: () => api.get('/insights/risks')
};

// LAB API (Enhanced THE LAB)
export const labAPI = {
  // Config
  getConfig: () => api.get('/lab/config'),
  
  // Threads
  getThreads: () => api.get('/lab/threads'),
  createThread: (data) => api.post('/lab/threads', data),
  createThreadV2: (data) => api.post('/lab/threads/v2', data),
  updateThread: (threadId, data) => api.put(`/lab/threads/${threadId}`, data),
  getMessages: (threadId) => api.get(`/lab/threads/${threadId}/messages`),
  sendMessage: (threadId, data) => api.post(`/lab/threads/${threadId}/messages`, data),
  sendMessageV2: (threadId, data) => api.post(`/lab/threads/${threadId}/messages/v2`, data),
  convertThread: (threadId) => api.post(`/lab/threads/${threadId}/convert`),
  
  // Recommendations
  getRecommendation: (text) => api.post('/lab/recommend', { text }),
  
  // Operators
  runOperator: (operatorId, answers, threadId = null) => 
    api.post('/lab/operators/run', { operator_id: operatorId, answers, thread_id: threadId }),
  
  // Artifacts
  getArtifacts: (threadId = null) => 
    api.get('/lab/artifacts', { params: threadId ? { thread_id: threadId } : {} }),
  getThreadArtifacts: (threadId) => api.get(`/lab/threads/${threadId}/artifacts`),
  createArtifact: (data) => api.post('/lab/artifacts', data),
  updateArtifact: (artifactId, data) => api.put(`/lab/artifacts/${artifactId}`, data),
  remixArtifact: (artifactId, tone) => api.post(`/lab/artifacts/${artifactId}/remix`, { tone }),
  
  // Convert Wizard
  convertPreview: (data) => api.post('/lab/convert-wizard/preview', data),
  convertExecute: (data) => api.post('/lab/convert-wizard/execute', data)
};

// Structure API
export const structureAPI = {
  // Org Tree (legacy hierarchical)
  getOrgTree: (params) => api.get('/structure/org-tree', { params }),
  
  // Tiered Org Chart (National Organizational Chart layout)
  getTieredChart: () => api.get('/structure/tiered-chart'),
  
  // Regional Org Chart (Regional Organizational Chart layout)
  getRegionalChart: (regionId = 'region_1') => api.get('/structure/regional-chart', { params: { region_id: regionId } }),
  
  // UNIQUE IP Org Chart (Soul Food organizational layout)
  getUniqueIPChart: (ipId = 'soul_food') => api.get('/structure/unique-ip-chart', { params: { ip_id: ipId } }),
  
  // Roles
  getRoles: (params) => api.get('/structure/roles', { params }),
  createRole: (data) => api.post('/structure/roles', data),
  updateRole: (id, data) => api.put(`/structure/roles/${id}`, data),
  archiveRole: (id) => api.delete(`/structure/roles/${id}`),
  validateReparent: (id, newParentId) => 
    api.post(`/structure/roles/${id}/validate-reparent`, { newParentId }),
  
  // Departments
  getDepartments: () => api.get('/structure/departments'),
  createDepartment: (data) => api.post('/structure/departments', data),
  updateDepartment: (id, data) => api.put(`/structure/departments/${id}`, data),
  
  // People
  getPeople: () => api.get('/structure/people'),
  createPerson: (data) => api.post('/structure/people', data),
  updatePerson: (id, data) => api.put(`/structure/people/${id}`, data),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/structure/audit-logs', { params })
};

// Team Directory API (Internal Staff)
export const teamDirectoryAPI = {
  // CRUD Operations
  getAll: (params = {}) => api.get('/team-directory', { params }),
  getOne: (memberId) => api.get(`/team-directory/${memberId}`),
  create: (data) => api.post('/team-directory', data),
  update: (memberId, data) => api.put(`/team-directory/${memberId}`, data),
  delete: (memberId) => api.delete(`/team-directory/${memberId}`),
  
  // Statistics
  getStats: () => api.get('/team-directory/stats'),
  
  // Bulk Operations
  bulkImport: (records, options = {}) => api.post('/team-directory/bulk-import', {
    records,
    validateOnly: options.validateOnly || false,
    skipDuplicates: options.skipDuplicates !== false
  }),
  validateImport: (records) => api.post('/team-directory/validate', {
    records,
    validateOnly: true
  })
};

// Threads API (Organization Communication)
export const threadsAPI = {
  // Thread Operations
  getAll: (includeArchived = false) => api.get('/threads', { params: { include_archived: includeArchived } }),
  getOne: (threadId) => api.get(`/threads/${threadId}`),
  create: (data) => api.post('/threads', data),
  update: (threadId, data) => api.put(`/threads/${threadId}`, data),
  archive: (threadId) => api.post(`/threads/${threadId}/archive`),
  markRead: (threadId) => api.post(`/threads/${threadId}/mark-read`),
  syncMembers: (threadId) => api.post(`/threads/${threadId}/sync-members`),
  
  // Messages
  getMessages: (threadId, limit = 100, beforeId = null) => 
    api.get(`/threads/${threadId}/messages`, { params: { limit, before_id: beforeId } }),
  sendMessage: (threadId, data) => api.post(`/threads/${threadId}/messages`, data),
  
  // Decisions
  getDecisions: (threadId) => api.get(`/threads/${threadId}/decisions`),
  markAsDecision: (messageId, isDecision = true) => 
    api.post(`/threads/messages/${messageId}/decision`, null, { params: { is_decision: isDecision } }),
  
  // Task Conversion
  convertToTask: (messageId, taskData) => api.post(`/threads/messages/${messageId}/convert-task`, taskData),
  
  // Search & Stats
  search: (query) => api.get('/threads/search', { params: { q: query } }),
  getUnreadCount: () => api.get('/threads/unread-count'),
  
  // Demo Data
  generateDemo: () => api.post('/threads/generate-demo')
};

// Asset API (Project Files & Deliverables)
export const assetAPI = {
  // Get all assets for a project
  getProjectAssets: (projectId, params = {}) => 
    api.get(`/projects/${projectId}/assets`, { params }),
  
  // Get asset summary for a project
  getProjectAssetSummary: (projectId) => 
    api.get(`/projects/${projectId}/assets/summary`),
  
  // Check if file exists (for versioning)
  checkVersion: (projectId, filename) => 
    api.get(`/projects/${projectId}/assets/check-version`, { params: { filename } }),
  
  // Upload asset (using FormData)
  uploadAsset: (projectId, formData) => 
    api.post(`/projects/${projectId}/assets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Get single asset
  getAsset: (assetId) => api.get(`/assets/${assetId}`),
  
  // Update asset metadata
  updateAsset: (assetId, data) => api.put(`/assets/${assetId}`, data),
  
  // Delete asset
  deleteAsset: (assetId) => api.delete(`/assets/${assetId}`)
};

// Phase API (Task Phases)
export const phaseAPI = {
  // Get all phases for a task
  getTaskPhases: (taskId) => api.get(`/tasks/${taskId}/phases`),
  
  // Get task phase summary
  getTaskPhasesSummary: (taskId) => api.get(`/tasks/${taskId}/phases/summary`),
  
  // Create a new phase
  createPhase: (taskId, data) => api.post(`/tasks/${taskId}/phases`, data),
  
  // Update a phase
  updatePhase: (phaseId, data) => api.put(`/phases/${phaseId}`, data),
  
  // Delete a phase
  deletePhase: (phaseId) => api.delete(`/phases/${phaseId}`),
  
  // Reorder phases
  reorderPhases: (taskId, phaseIds) => api.post(`/tasks/${taskId}/phases/reorder`, phaseIds)
};

// Workstream API (Project Sub-Projects)
export const workstreamAPI = {
  // Get all workstreams for a project
  getProjectWorkstreams: (projectId) => api.get(`/projects/${projectId}/workstreams`),
  
  // Create a new workstream
  createWorkstream: (projectId, data) => api.post(`/projects/${projectId}/workstreams`, data),
  
  // Get a single workstream
  getWorkstream: (workstreamId) => api.get(`/workstreams/${workstreamId}`),
  
  // Update a workstream
  updateWorkstream: (workstreamId, data) => api.put(`/workstreams/${workstreamId}`, data),
  
  // Delete a workstream
  deleteWorkstream: (workstreamId) => api.delete(`/workstreams/${workstreamId}`),
  
  // Assign task to workstream
  assignTask: (taskId, workstreamId) => api.post(`/tasks/${taskId}/workstream`, null, {
    params: { workstream_id: workstreamId }
  }),
  
  // Get workstream suggestions
  getSuggestions: (projectId) => api.get(`/projects/${projectId}/workstream-suggestions`)
};

// Focus / Executive Command Center API
export const focusAPI = {
  // Get command center data
  getCommandCenter: () => api.get('/focus/command-center'),
  
  // Toggle focus item completion
  toggleItem: (itemId) => api.post(`/focus/toggle-item/${itemId}`),
  
  // Schedule a meeting
  scheduleMeeting: (data) => api.post('/focus/schedule-meeting', data),
  
  // Delegate a task
  delegateTask: (data) => api.post('/focus/delegate-task', data),
  
  // Send a message
  sendMessage: (data) => api.post('/focus/send-message', data),
  
  // Flag an item
  flagItem: (data) => api.post('/focus/flag-item', data),
  
  // Acknowledge an alert
  acknowledgeAlert: (data) => api.post('/focus/acknowledge-alert', data),
  
  // Get team for delegation
  getTeamForDelegation: () => api.get('/focus/team-for-delegation'),
  
  // Get projects for task assignment
  getProjectsForTask: () => api.get('/focus/projects-for-task')
};

// Executive Review API
export const reviewAPI = {
  // Get projects awaiting review
  getAwaitingReview: (reviewerId = null) => 
    api.get('/reviews/awaiting', { params: reviewerId ? { reviewer_id: reviewerId } : {} }),
  
  // Get review info for a specific project
  getProjectReviewInfo: (projectId) => api.get(`/reviews/project/${projectId}`),
  
  // Get review history for a project
  getProjectReviewHistory: (projectId) => api.get(`/reviews/project/${projectId}/history`),
  
  // Submit project for review
  submitForReview: (projectId, notes = null) => 
    api.post('/reviews/submit', { project_id: projectId, notes }),
  
  // Make decision (approve or request revisions)
  makeDecision: (projectId, decision, comment, createFollowupTask = true) => 
    api.post('/reviews/decision', { 
      project_id: projectId, 
      decision, 
      comment,
      create_followup_task: createFollowupTask 
    }),
  
  // Reassign reviewer
  reassignReviewer: (projectId, newReviewerId, reason = null) => 
    api.post('/reviews/reassign', { 
      project_id: projectId, 
      new_reviewer_id: newReviewerId, 
      reason 
    }),
  
  // Get eligible reviewers
  getEligibleReviewers: () => api.get('/reviews/eligible-reviewers'),
  
  // Get pipeline metrics (for Insights)
  getPipelineMetrics: () => api.get('/reviews/pipeline-metrics'),
  
  // Get pulse metrics (for Leadership Pulse)
  getPulseMetrics: () => api.get('/reviews/pulse-metrics')
};

// Predictive Analytics API
export const analyticsAPI = {
  // Team Member Analytics (for Team page)
  getMemberAnalytics: (memberId) => api.get(`/analytics/member/${memberId}`),
  getMemberBurnoutRisk: (memberId) => api.get(`/analytics/member/${memberId}/burnout`),
  getMemberPromotionReadiness: (memberId) => api.get(`/analytics/member/${memberId}/promotion`),
  getMemberLaneDrift: (memberId) => api.get(`/analytics/member/${memberId}/drift`),
  
  // Burnout Alerts (for Focus page)
  getBurnoutAlerts: () => api.get('/analytics/burnout-alerts'),
  getBurnoutHeatmap: () => api.get('/analytics/burnout-heatmap'),
  
  // Organization Analytics (for Insights page)
  getOrgDashboard: () => api.get('/analytics/org/dashboard'),
  getStructuralFragility: () => api.get('/analytics/org/fragility'),
  getDecisionBottleneck: () => api.get('/analytics/org/bottleneck'),
  getCrossLaneTension: () => api.get('/analytics/org/tension'),
  getCognitiveHeatmap: () => api.get('/analytics/org/heatmap'),
  
  // Expansion Simulation (for Structure page)
  simulateExpansion: (data) => api.post('/analytics/org/simulate-expansion', data)
};

export default api;
